import React, { useState } from 'react';
import { supabaseClient } from '../../../shared/utils/supabaseClient';
import { PAYMENT_STATUSES } from '../../../shared/constants/paymentStatuses';
import ProofThumbnails from '../../../shared/components/ProofThumbnails';

const MemberSubmissionReviewModal = ({ hand, payment, member, onClose, onSuccess }) => {
  const [rejecting, setRejecting] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleApprove = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      const now = new Date().toISOString();

      const { error: updateErr } = await supabaseClient
        .from('payments')
        .update({
          status: PAYMENT_STATUSES.PAID,
          amount_received: payment.submitted_amount,
          payment_method: payment.submitted_method,
          reference_number: payment.submitted_reference,
          payment_date: now.split('T')[0],
          covered_by_admin: false,
        })
        .eq('id', payment.id);
      if (updateErr) throw updateErr;

      // The payment_approved notification is now created SERVER-SIDE by the
      // notify_payer_on_confirm trigger (Stage 44) on the status -> 'paid' transition,
      // so it fires on BOTH confirm paths (this admin update AND the collector's confirm
      // RPC). No client insert here → no duplicate.

      await supabaseClient.from('audit_logs').insert({
        event_category: 'financial',
        event_type: 'payment_approved',
        actor_id: user.id,
        actor_role: 'admin',
        old_value: { status: PAYMENT_STATUSES.MEMBER_SUBMITTED },
        new_value: { status: PAYMENT_STATUSES.PAID, amount_received: payment.submitted_amount },
        risk_level: 'low',
        flagged: false,
        timestamp: now,
      });

      onSuccess();
    } catch (err) {
      console.error('Approve error:', err);
      setError('Failed to approve payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      setError('Rejection reason is required');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data: { user } } = await supabaseClient.auth.getUser();
      const now = new Date().toISOString();

      const { error: updateErr } = await supabaseClient
        .from('payments')
        .update({
          status: PAYMENT_STATUSES.REJECTED,
          rejection_reason: rejectReason.trim(),
        })
        .eq('id', payment.id);
      if (updateErr) throw updateErr;

      // NON-FATAL notify: group_id in metadata; the Stage 42 trigger fills the column.
      if (member?.auth_user_id) {
        try {
          const { error: notifErr } = await supabaseClient.from('notifications').insert({
            user_id: member.auth_user_id,
            type: 'payment_rejected',
            message: `Your payment for Hand ${hand.position_number} was rejected: ${rejectReason.trim()}`,
            read: false,
            metadata: {
              hand_id: hand.id,
              payment_id: payment.id,
              group_id: payment.group_id ?? hand.group_id,
              reason: rejectReason.trim(),
            },
          });
          if (notifErr) console.error('notification insert failed (non-fatal):', notifErr);
        } catch (notifErr) { console.error('notification step failed (non-fatal):', notifErr); }
      }

      await supabaseClient.from('audit_logs').insert({
        event_category: 'financial',
        event_type: 'payment_rejected',
        actor_id: user.id,
        actor_role: 'admin',
        old_value: { status: PAYMENT_STATUSES.MEMBER_SUBMITTED },
        new_value: { status: PAYMENT_STATUSES.REJECTED, rejection_reason: rejectReason.trim() },
        risk_level: 'medium',
        flagged: true,
        timestamp: now,
      });

      onSuccess();
    } catch (err) {
      console.error('Reject error:', err);
      setError('Failed to reject submission. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-neutral-100 rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-md">
        <div className="w-10 h-1 bg-neutral-300 rounded-full mx-auto mb-4 sm:hidden" />
        <div className="flex justify-between items-center mb-1">
          <h3 className="text-lg font-semibold">Review Submission</h3>
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-600 text-xl">✕</button>
        </div>
        <p className="text-sm text-neutral-500 mb-4">{member?.name} — Hand {hand.position_number}</p>

        <div className="bg-neutral-100 border border-accent-500 rounded-lg p-4 mb-4 space-y-2">
          <p className="text-xs font-semibold text-accent-400 uppercase tracking-wide mb-2">Member's Submission</p>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-600">Amount</span>
            <span className="font-medium text-neutral-900">${payment.submitted_amount?.toFixed(2)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-600">Method</span>
            <span className="font-medium capitalize text-neutral-900">{payment.submitted_method}</span>
          </div>
          <div className="flex justify-between text-sm gap-2">
            <span className="text-neutral-600 flex-shrink-0">Reference</span>
            <span className="font-medium text-neutral-900 text-right break-all min-w-0">{payment.submitted_reference}</span>
          </div>
          {payment.submitted_notes && (
            <div className="flex justify-between text-sm gap-2">
              <span className="text-neutral-600 flex-shrink-0">Notes</span>
              <span className="font-medium text-neutral-900 text-right break-all min-w-0">{payment.submitted_notes}</span>
            </div>
          )}
          <div className="flex justify-between text-sm">
            <span className="text-neutral-600">Submitted</span>
            <span className="font-medium text-neutral-900">
              {payment.submitted_at ? new Date(payment.submitted_at).toLocaleString() : '—'}
            </span>
          </div>
          <div className="text-sm pt-2 mt-1 border-t border-accent-500/30">
            <p className="text-neutral-600 mb-1">Screenshot proof(s)</p>
            {/* All receipts for this submission (primary + split-payment images).
                Review against the Amount + Reference above before confirming. */}
            {payment.proof_path || payment.id ? (
              <ProofThumbnails paymentId={payment.id} primaryPath={payment.proof_path} />
            ) : (
              <p className="text-neutral-400 text-xs">No proof attached.</p>
            )}
          </div>
        </div>

        {error && <p className="text-error-600 text-sm mb-3">{error}</p>}

        {rejecting ? (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-neutral-700 mb-1">Rejection Reason *</label>
              <textarea
                value={rejectReason}
                onChange={e => setRejectReason(e.target.value)}
                className="w-full border border-neutral-300 rounded-lg px-3 py-2 text-sm"
                rows={3}
                placeholder="Explain why this submission is being rejected..."
                autoFocus
              />
            </div>
            <div className="flex space-x-2">
              <button
                onClick={handleReject}
                disabled={loading}
                className="flex-1 px-4 py-3 bg-error-600 text-white rounded-lg text-base font-medium disabled:opacity-50"
              >
                {loading ? 'Rejecting...' : 'Confirm Rejection'}
              </button>
              <button
                onClick={() => { setRejecting(false); setRejectReason(''); setError(null); }}
                className="flex-1 px-4 py-3 border border-neutral-300 rounded-lg text-base text-neutral-700"
              >
                Back
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col space-y-2">
            <button
              onClick={handleApprove}
              disabled={loading}
              className="w-full px-4 py-3 bg-success-600 text-white rounded-lg text-base font-medium disabled:opacity-50"
            >
              {loading ? 'Approving...' : 'Approve Payment'}
            </button>
            <button
              onClick={() => setRejecting(true)}
              disabled={loading}
              className="w-full px-4 py-3 bg-error-100 text-error-700 rounded-lg text-base font-medium hover:bg-error-200 disabled:opacity-50"
            >
              Reject Submission
            </button>
            <button
              onClick={onClose}
              className="w-full px-4 py-3 border border-neutral-300 rounded-lg text-base text-neutral-700"
            >
              Cancel
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberSubmissionReviewModal;
